import type { ContactFormData, SubmitError } from './types';
import { liveAgentHours } from '@v2/shared/lib/supportHours';

/**
 * The twenty reasons the dropdown offers, in two groups, in the order they are listed.
 *
 * These labels are not the subject titles. A title is Kustomer's routing key, written
 * for an agent reading a queue -- "DiscountLock related inquiry", "Cancel/Refund my
 * Magazine(s)" -- and renaming one silently re-routes live tickets, so they cannot be
 * made to read like a customer's own words. These are those words, mapped onto the same
 * ids, and the id is all that is posted: the server looks the title up again itself.
 *
 * So the two lists are allowed to differ, and the ids are the contract between them.
 * Which is also why the page filters this list against the ids the server sent rather
 * than trusting it -- see ContactPageProps['subjects'].
 */
export const REASON_GROUPS: { label: string; reasons: { id: number; label: string }[] }[] = [
  {
    label: 'About an order or subscription',
    reasons: [
      { id: 27, label: "A charge I don't recognize, or a renewal question" },
      { id: 26, label: 'Stop my subscription from renewing' },
      { id: 24, label: 'Cancel my order and get a refund' },
      { id: 8, label: 'Where is my first issue?' },
      { id: 31, label: 'I missed an issue, or it stopped coming' },
      { id: 22, label: "I'm getting duplicate copies" },
      { id: 7, label: 'Change my address or name' },
      { id: 9, label: 'Fix a mistake in my order (wrong title, term or recipient)' },
      { id: 35, label: 'Update my payment card' },
      { id: 29, label: 'Digital magazine access (Zinio, Magzter, The Economist)' },
      { id: 34, label: 'Book or other goods order' },
      { id: 36, label: 'Price question or price match' },
      { id: 38, label: 'The subscriber has passed away' },
    ],
  },
  {
    label: 'Something else',
    reasons: [
      { id: 16, label: 'Trouble ordering or a website problem' },
      { id: 30, label: "A coupon, gift certificate or reward didn't apply" },
      { id: 37, label: 'Delete my account or a privacy request' },
      { id: 17, label: 'Bulk or purchase order' },
      { id: 10, label: 'Affiliate and marketing' },
      { id: 11, label: 'Publisher relations' },
      { id: 6, label: 'Something else' },
    ],
  },
];

/**
 * The reasons that are about an order, so the form knows when it is worth asking for one.
 *
 * Read by the "Can't find your order?" prompt: a question about a charge or a missing
 * issue cannot be answered without finding the order first, and a message with no order
 * number, no name and no ZIP costs a round trip by email before anyone can start.
 * Affiliate and publisher enquiries have no order at all and are never nudged.
 */
export const ORDER_REASON_IDS: number[] = REASON_GROUPS[0].reasons.map((reason) => reason.id);

/** The reason a card's "message us" link selects, so a card and the dropdown agree. */
export const REASON = {
  charge: 27,
  missedIssue: 31,
  cancel: 24,
  address: 7,
  duplicates: 22,
  coupon: 30,
  publishers: 11,
} as const;

/** The account pages the topics send people to, which is where most of this is self-serve. */
export const ORDERS_URL = '/customer?xCmd=orders';
export const DISCOUNTLOCK_URL = '/my-account/discountlock';
export const SIGN_IN_URL = '/customer?xCmd=login';

/** How long a first issue takes, by how often the magazine comes out. */
export const DELIVERY_ESTIMATES: [string, string][] = [
  ['Weekly', '6 to 8 weeks'],
  ['Bi-weekly and monthly', '8 to 10 weeks'],
  ['Bi-monthly and seasonal', '8 to 12 weeks'],
  ['Quarterly or less often', '10 to 12 weeks'],
];

/**
 * The two phone lines and what each is for. A function rather than a constant: the live
 * agents' hours move with Central's daylight saving (see supportHours), and a constant
 * would keep whatever they were when the server started.
 */
export function phoneLines(now: Date = new Date()): [string, string, string][] {
  return [
    ['Automated order line', '24/7', 'Check order status, update your address, manage renewal settings'],
    ['Live agents', liveAgentHours(now), "For anything the automated line can't finish"],
  ];
}

/** The jump links above the page, and the sections they land on. */
export const JUMP_LINKS: [string, string][] = [
  ['#topics', 'Pick a topic'],
  ['#message', 'Send a message'],
  ['#phone', 'Call us'],
  ['#signin', "Can't sign in?"],
  ['#faqs', 'Quick answers'],
];

/**
 * The three ways contact.php can refuse a submission, in this page's words.
 *
 * Same wording the other two V2 forms posting to this endpoint use.
 */
export const ERROR_MESSAGES: Record<NonNullable<SubmitError>, string> = {
  captcha: "We couldn't verify you're not a robot. Please try again.",
  ticket: 'An unexpected error occurred, please try again.',
  validation: 'Please fix the errors indicated below.',
};

/** Each required field's own message, quoted by the browser's rules and by the server's. */
export const FIELD_MESSAGES: Record<
  Exclude<keyof ContactFormData, 'order' | 'magazines' | 'recipientName' | 'recipientZip'>,
  string
> = {
  name: 'Please enter your name',
  EmailAddress: 'Please enter a valid email address',
  Subject: 'Please choose a topic',
  cfComments: 'Please enter your Message.',
};

/**
 * The per-field flags contact.php sets on a refusal, and the field each belongs to.
 *
 * Without this every server refusal read as "Please fix the errors indicated below" with
 * nothing indicated below -- reachable whenever the server is stricter than the browser,
 * which it is: `chop()` before NOTBLANK rejects a name of spaces that `required` accepts.
 */
export const SERVER_FIELD_ERRORS: Record<string, keyof typeof FIELD_MESSAGES> = {
  name_error: 'name',
  EmailAddress_error: 'EmailAddress',
  Subject_error: 'Subject',
  cfComments_error: 'cfComments',
};

/** What a DiscountMags order number looks like: two letters, seven digits. */
export const ORDER_NUMBER_PATTERN = /^[A-Z]{2}\d{7}$/;
